import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AIRPORT_SORT_FIELDS } from '@/types/airport';
import type { AirportSortField } from '@/types/airport';
import type { AirportCriteria } from './api';

/**
 * The dictionary table's state, held in the URL and nowhere else.
 *
 * Same rule as the flight board: THE URL IS THE STATE — read on render, written on
 * an action, never mirrored into React state. A refresh keeps the filters, a link
 * carries them, and Back works without a line of code for it.
 *
 * Unlike the user list, every one of these IS a server query: `/airport/list` filters,
 * sorts and pages about 9 400 rows, so nothing here is a narrowing of something
 * already in the browser.
 */

/** Short names, because this query string is meant to be read and pasted. */
const KEYS = {
  query: 'q',
  country: 'country',
  city: 'city',
  iataCode: 'iata',
  icaoCode: 'icao',
  active: 'active',
  page: 'page',
  rows: 'rows',
  sort: 'sort',
  dir: 'dir',
  /** The panel: an airport id, or `new` for the create form. */
  id: 'id',
} as const;

const CREATE = 'new';

/** The server caps a page at 200 rows and falls back to 50 for anything invalid. */
export const ROWS_OPTIONS = [25, 50, 100, 200] as const;
const DEFAULT_ROWS = 50;

/** The dictionary is read by code, which is also the server's own default. */
const DEFAULT_SORT: AirportSortField = 'iataCode';

export type Selection =
  | { mode: 'closed' }
  | { mode: 'create' }
  | { mode: 'edit'; id: number };

export interface AirportParams {
  query: string;
  country: string;
  city: string;
  iataCode: string;
  icaoCode: string;
  /** `''` means both — the parameter is omitted rather than sent as a default. */
  active: 'true' | 'false' | '';
  page: number;
  rows: number;
  sort: AirportSortField;
  dir: 'ascending' | 'descending';
  selection: Selection;
}

const isOneOf = <T extends string>(options: readonly T[], value: string | null): value is T =>
  value !== null && (options as readonly string[]).includes(value);

export const useAirportParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const params = useMemo<AirportParams>(() => {
    const text = (key: string) => searchParams.get(key)?.trim() ?? '';

    const rows = Number(searchParams.get(KEYS.rows));
    const page = Number(searchParams.get(KEYS.page));
    const sort = searchParams.get(KEYS.sort);
    const active = searchParams.get(KEYS.active);
    const selected = searchParams.get(KEYS.id);
    const id = Number(selected);

    return {
      query: text(KEYS.query),
      country: text(KEYS.country),
      city: text(KEYS.city),
      iataCode: text(KEYS.iataCode),
      icaoCode: text(KEYS.icaoCode),
      active: active === 'true' || active === 'false' ? active : '',
      // A page or size that is not a positive number falls back rather than being
      // sent: the server would substitute its own default anyway, and then the pager
      // on screen would be describing a page size nobody asked for.
      page: Number.isFinite(page) && page >= 1 ? Math.floor(page) : 1,
      rows: Number.isFinite(rows) && rows >= 1 ? Math.min(Math.floor(rows), 200) : DEFAULT_ROWS,
      // Validated here rather than left to the server: an unknown `sortBy` is a 400
      // naming the allowed fields, so a hand-edited or stale URL would break the
      // whole table instead of ordering it differently.
      sort: isOneOf(AIRPORT_SORT_FIELDS, sort) ? sort : DEFAULT_SORT,
      dir: searchParams.get(KEYS.dir) === 'descending' ? 'descending' : 'ascending',
      selection:
        selected === CREATE
          ? { mode: 'create' }
          : Number.isInteger(id) && id > 0
            ? { mode: 'edit', id }
            : { mode: 'closed' },
    };
  }, [searchParams]);

  /**
   * Applies a change to the filters, the page or the order.
   *
   * Any change other than the page itself resets to page 1: a narrower filter
   * otherwise leaves the reader on a page that no longer exists, and this API
   * answers with the LAST page rather than an empty one — which reads as the filter
   * having been ignored.
   *
   * `replace`, so typing in a filter does not bury the page the reader arrived from
   * under one history entry per keystroke.
   */
  const update = useCallback(
    (changes: Partial<Omit<AirportParams, 'selection'>>) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);

          for (const [key, value] of Object.entries(changes)) {
            // The field's name and its parameter's name are not the same — `query`
            // is `q` in the URL, because this query string is meant to be read.
            const param = KEYS[key as keyof typeof KEYS];

            if (value === '' || value === undefined || value === null) next.delete(param);
            else next.set(param, String(value));
          }

          if (!('page' in changes)) next.delete(KEYS.page);

          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  /** Opening or closing the panel is not a filter change and leaves the page alone. */
  const select = useCallback(
    (selection: Selection) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);

          if (selection.mode === 'closed') next.delete(KEYS.id);
          else if (selection.mode === 'create') next.set(KEYS.id, CREATE);
          else next.set(KEYS.id, String(selection.id));

          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const hasFilters =
    params.query !== '' ||
    params.country !== '' ||
    params.city !== '' ||
    params.iataCode !== '' ||
    params.icaoCode !== '' ||
    params.active !== '';

  const reset = useCallback(
    () => update({ query: '', country: '', city: '', iataCode: '', icaoCode: '', active: '' }),
    [update],
  );

  /**
   * The request for these params.
   *
   * `active` becomes a real boolean or is left out entirely — the endpoint treats an
   * absent parameter as "both", and there is no third value to send for it. `id` is
   * a UI concern and never travels.
   */
  const criteria = useMemo<AirportCriteria>(
    () => ({
      query: params.query,
      country: params.country,
      city: params.city,
      iataCode: params.iataCode,
      icaoCode: params.icaoCode,
      ...(params.active === '' ? {} : { active: params.active === 'true' }),
      pageNum: params.page,
      rows: params.rows,
      sortBy: params.sort,
      sortDirection: params.dir,
    }),
    [params],
  );

  return { params, criteria, update, select, reset, hasFilters };
};
